import React from 'react';
import type { Table, Order, Reservation } from '../../types';
import { orderHasMeaningfulItems, getTableSeatsUsed } from '../../lib/orderUtils';
import ClockIcon from '../icons/ClockIcon';
import UsersIcon from '../icons/UsersIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import XCircleIcon from '../icons/XCircleIcon';
import BuffetTimer from './BuffetTimer';

interface TableDetailsModalProps {
  table: Table;
  orders: Order[];
  reservations?: Reservation[];
  onClose: () => void;
  /** When true, only show reservations and guest status for the day (no orders/seat actions) */
  reservationOnly?: boolean;
  /** When set (e.g. from Reservations page), filter reservations to this date only */
  reservationDate?: string;
  onSeatNewParty?: (table: Table) => void;
  onSelectOrder?: (order: Order) => void;
  onEndBuffetSession?: (order: Order) => void;
}

const TableDetailsModal: React.FC<TableDetailsModalProps> = ({
  table,
  orders,
  reservations = [],
  onClose,
  reservationOnly = false,
  reservationDate,
  onSeatNewParty,
  onSelectOrder,
  onEndBuffetSession,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const todayKey = new Date().toDateString();
  // Active orders at this table today: dine-in unpaid + buffet (session active)
  const tableOrders = orders.filter(o => {
    if (o.tableId !== table.id) return false;
    if (o.status === 'cancelled' || o.status === 'refunded') return false;
    if (!orderHasMeaningfulItems(o)) return false;
    const orderDateKey = new Date(o.timestamp).toDateString();
    if (orderDateKey !== todayKey) return false;
    if (o.orderType === 'buffet') return o.status === 'buffet';
    if (o.orderType === 'dine-in') return o.paymentStatus !== 'paid';
    return false;
  });
  const seatsUsed = getTableSeatsUsed(tableOrders);
  const spareSeats = Math.max(0, table.capacity - seatsUsed);

  // Calculate totals
  const totalAmount = tableOrders.reduce((sum, order) => sum + order.totalAmount, 0);
  const totalItems = tableOrders.reduce((sum, order) => 
    sum + order.items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0
  );

  const upcomingReservations = reservations
    .filter((r) => {
      if (r.tableId !== table.id || (r.status !== 'pending' && r.status !== 'confirmed')) return false;
      if (reservationDate) return r.reservationDate === reservationDate;
      return r.reservationDate >= today;
    })
    .sort((a, b) => {
      const dateCmp = a.reservationDate.localeCompare(b.reservationDate);
      if (dateCmp !== 0) return dateCmp;
      return a.reservationTime.localeCompare(b.reservationTime);
    });

  // Format elapsed time (mm:ss)
  const formatElapsedTime = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format "Arrived at" time
  const formatArrivedAt = (timestamp: number) => {
    const d = new Date(timestamp);
    return d.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  // Get order status badge
  const getStatusBadge = (status: string) => {
    const statusClasses: Record<string, string> = {
      'new': 'bg-blue-100 text-blue-700 border-blue-300',
      'preparing': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'ready': 'bg-green-100 text-green-700 border-green-300',
      'served': 'bg-purple-100 text-purple-700 border-purple-300',
      'buffet': 'bg-purple-100 text-purple-700 border-purple-300',
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-pink-500 to-pink-600 text-white">
          <div>
            <h2 className="text-xl font-bold">Table {table.name}</h2>
            <p className="text-sm text-pink-100">
              {reservationOnly ? `Capacity: ${table.capacity}` : (
                <>Capacity: {table.capacity} • {seatsUsed} seat{seatsUsed !== 1 ? 's' : ''} used
                {spareSeats > 0 && <> • <span className="text-green-200">{spareSeats} spare</span></>}</>
              )}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="text-white hover:text-pink-200 transition-colors"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Upcoming reservations for this table */}
          {upcomingReservations.length > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-amber-900 mb-2">
                {reservationOnly ? 'Reservations for this table' : 'Upcoming reservations for this table'}
              </h3>
              <ul className="space-y-1 text-sm text-amber-900">
                {upcomingReservations.map((r) => (
                  <li key={r.id} className="flex justify-between">
                    <span>
                      {r.reservationDate === today ? 'Today' : r.reservationDate}{' '}
                      at {r.reservationTime} • {r.customerName} ({r.partySize})
                    </span>
                    <span className="text-xs uppercase font-semibold">
                      {r.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {reservationOnly && (
            <>
              {upcomingReservations.length === 0 && (
                <p className="text-gray-500 text-sm mb-4">No reservations for this table.</p>
              )}
              <div className="flex justify-end">
                <button onClick={onClose} className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-semibold">
                  Close
                </button>
              </div>
            </>
          )}

          {!reservationOnly && tableOrders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No active orders for this table</p>
            </div>
          ) : !reservationOnly ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Total Items</p>
                    <p className="text-2xl font-bold text-gray-800">{totalItems}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-2xl font-bold text-pink-600">${totalAmount.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Customer Orders */}
              {tableOrders.map((order, orderIndex) => {
                const customerLabel = order.customerName || `Customer ${order.customerNumber || orderIndex + 1}`;
                const elapsedTime = formatElapsedTime(order.timestamp);
                const pendingItems = order.items.filter(item => 
                  item.status === 'pending' && 
                  !(item.price < 0 || item.id.toString().includes('combo-discount'))
                );
                const completedItems = order.items.filter(item => 
                  item.status === 'completed' && 
                  !(item.price < 0 || item.id.toString().includes('combo-discount'))
                );
                const servedItems = order.items.filter(item => 
                  item.served === true && 
                  !(item.price < 0 || item.id.toString().includes('combo-discount'))
                );

                const isBuffet = order.orderType === 'buffet';
                
                return (
                  <div key={order.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                    {/* Customer Header */}
                    <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-200">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <UsersIcon className="w-4 h-4 text-pink-600" />
                          <h3 className="font-bold text-lg text-gray-800">
                            {isBuffet 
                              ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${customerLabel}`
                              : customerLabel}
                          </h3>
                          {isBuffet && (
                            <span className="text-xs font-bold px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                              BUFFET
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center gap-1" title="Arrived">
                            <ClockIcon className="w-3 h-3" />
                            Arrived {formatArrivedAt(order.timestamp)}
                          </span>
                          <span className="flex items-center gap-1" title="Elapsed">
                            Elapsed {elapsedTime}
                          </span>
                          {isBuffet && order.buffetEndTime && (
                            <span className="flex items-center gap-1">
                              <BuffetTimer endTime={order.buffetEndTime} />
                              <span className="text-xs text-purple-600">(time remaining)</span>
                            </span>
                          )}
                          {order.servingStaffName && (
                            <span className="text-xs text-gray-500">Served by {order.servingStaffName}</span>
                          )}
                          <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusBadge(order.status)}`}>
                            {order.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Order Total</p>
                        <p className="text-lg font-bold text-pink-600">${order.totalAmount.toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Buffet: time left + End early (no Pending/Ready/Served) */}
                    {isBuffet && (
                      <div className="mb-3 space-y-3">
                        {order.buffetEndTime && (
                          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                            <p className="text-sm font-semibold text-purple-800 mb-2">Time left</p>
                            <div className="text-2xl font-bold text-purple-700">
                              <BuffetTimer endTime={order.buffetEndTime} className="text-lg" />
                            </div>
                          </div>
                        )}
                        {onEndBuffetSession && order.status === 'buffet' && (
                          <button
                            type="button"
                            onClick={() => { onClose(); onEndBuffetSession(order); }}
                            className="w-full py-2.5 px-3 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            End session early
                          </button>
                        )}
                      </div>
                    )}

                    {/* Dine-in: Order Items Summary (Pending / Ready / Served) */}
                    {!isBuffet && (
                      <>
                        <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                          <div className="bg-blue-50 rounded p-2 text-center border border-blue-200">
                            <p className="text-blue-700 font-semibold">{pendingItems.length}</p>
                            <p className="text-blue-600">Pending</p>
                          </div>
                          <div className="bg-yellow-50 rounded p-2 text-center border border-yellow-200">
                            <p className="text-yellow-700 font-semibold">{completedItems.length}</p>
                            <p className="text-yellow-600">Ready</p>
                          </div>
                          <div className="bg-green-50 rounded p-2 text-center border border-green-200">
                            <p className="text-green-700 font-semibold">{servedItems.length}</p>
                            <p className="text-green-600">Served</p>
                          </div>
                        </div>

                        {/* Items List */}
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Items ({order.items.filter(item => !(item.price < 0 || item.id.toString().includes('combo-discount'))).length}):</p>
                          <div className="max-h-48 overflow-y-auto space-y-1">
                            {order.items
                              .filter(item => !(item.price < 0 || item.id.toString().includes('combo-discount')))
                              .map((item, itemIndex) => (
                                <div 
                                  key={`${item.id}-${itemIndex}`}
                                  className="flex items-start justify-between text-sm p-2 bg-gray-50 rounded border border-gray-100"
                                >
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-gray-800">{item.quantity}x</span>
                                      <span className="text-gray-700">{item.name}</span>
                                      {item.isRemake && (
                                        <span className="text-xs font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded border border-purple-300">
                                          REMAKE
                                        </span>
                                      )}
                                    </div>
                                    {item.selectedMeatOption && (
                                      <p className="text-xs text-pink-600 mt-0.5 ml-6">Meat: {item.selectedMeatOption}</p>
                                    )}
                                    {item.notes && (
                                      <p className="text-xs text-orange-600 mt-0.5 ml-6 font-medium">Note: {item.notes}</p>
                                    )}
                                    {item.isRemake && item.remakeReason && (
                                      <p className="text-xs text-purple-600 mt-0.5 ml-6">Reason: {item.remakeReason}</p>
                                    )}
                                  </div>
                                  <div className="text-right ml-2">
                                    <p className="font-semibold text-gray-800">${(item.price * item.quantity).toFixed(2)}</p>
                                    <div className="flex items-center gap-1 mt-1">
                                      {item.status === 'completed' && (
                                        <CheckCircleIcon className="w-3 h-3 text-green-600" />
                                      )}
                                      {item.served && (
                                        <span className="text-xs text-green-600 font-semibold">Served</span>
                                      )}
                                      {item.status === 'pending' && (
                                        <span className="text-xs text-blue-600 font-semibold">Pending</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Order Notes */}
                    {order.notes && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Order Notes:</p>
                        <p className="text-sm text-gray-600 bg-yellow-50 p-2 rounded border border-yellow-200">
                          {order.notes}
                        </p>
                      </div>
                    )}

                    {/* View / Edit order */}
                    {onSelectOrder && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => onSelectOrder(order)}
                          className="w-full py-2 px-3 bg-pink-500 text-white text-sm font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                        >
                          View / Edit order
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        {/* Footer – only when not reservation-only */}
        {!reservationOnly && (
          <div className="p-4 border-t bg-gray-50 flex flex-wrap justify-between items-center gap-2">
            <div>
              {spareSeats > 0 && onSeatNewParty && (
                <button
                  type="button"
                  onClick={() => { onClose(); onSeatNewParty(table); }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm"
                >
                  Seat new party ({spareSeats} seat{spareSeats !== 1 ? 's' : ''} available)
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors font-semibold"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableDetailsModal;

